/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { ReactElement } from "react";

import * as React from "react";
import styled from "styled-components";
import GlobeGrid from "./globe-grid.svg";
import {
  SharedPicker,
  SharedPickerOption,
} from "components/shared/SharedPicker";

import { changeLocale } from "utilities/UserSettingsUtilities";
import { useLocale } from "components/LocaleContext";

const QA_LANGUAGE_ENABLED = process.env.QA_LANGUAGE_ENABLED === "true";

const LanguagePickerContainer = styled.div`
  display: flex;
  align-items: center;
  z-index: 999;
`;

export const LanguagePicker: React.FC = (): ReactElement => {
  const currentLocale = useLocale();
  const supportedLanguages: SharedPickerOption[] = [
    { value: "de", name: "Deutsch (Deutschland)" },
    { value: "en", name: "English (United States)" },
    { value: "es", name: "Español (España)" },
    { value: "fr", name: "Français (France)" },
    { value: "it", name: "Italiano (Italia)" },
    { value: "pt", name: "Português (Brasil)" },
    { value: "bn", name: "বাংলা (ভারত)" },
    { value: "hi", name: "हिन्दी (भारत)" },
    { value: "id", name: "Indonesia (Indonesia)" },
    { value: "ph", name: "Filipino (Pilipinas)" },
    { value: "tr", name: "Türkçe (Türkiye)" },
  ];
  // Add QA language if enabled STAGE ONLY
  if (QA_LANGUAGE_ENABLED) {
    supportedLanguages.push({ value: "en-ZA", name: "QA (Test Locale)" });
    // supportedLanguages.push(); //Add qa locales here and uncomment
    supportedLanguages.sort((a, b) => (a.value > b.value ? 1 : -1));
  }

  return (
    <LanguagePickerContainer>
      <sp-field-label size={"xl"} sideAligned="start">
        <img src={GlobeGrid} />
      </sp-field-label>
      <SharedPicker
        options={supportedLanguages}
        selectedValue={currentLocale}
        onOptionSelected={(locale): void => {
          changeLocale(locale).catch((error) => {
            console.error("Error while setting user language:", error);
            newrelic.noticeError(error);
          });
        }}
        isQuiet={true}
        pickerStyles={{ marginLeft: 12, marginTop: 8 }}
        pickerSize={"xl"}
      />
    </LanguagePickerContainer>
  );
};
